import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';

import { useContentReportTableData } from './hooks/useContentReportTableData.ts';
import {
	useParsedMacroParams,
	type ContentReportTableMacroParams,
} from './hooks/useParsedMacroParams.ts';
import { i18n } from './contentReportTableMacroI18n.ts';
import { buildCreateContentUrl, buildFindMoreResultsUrl } from './contentReportTableUrlUtils.ts';
import { ContentReportTable } from './components/ContentReportTable.tsx';
import { EmptyState } from './components/EmptyState.tsx';

export type ContentReportTableMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: ContentReportTableMacroParams;
	canViewProfiles?: boolean;
};

const styles = cssMap({
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		paddingBlock: token('space.300'),
	},
});

const ContentReportTableMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
	canViewProfiles = true,
}: ContentReportTableMacroRendererProps) => {
	const { formatMessage } = useIntl();

	const {
		labels,
		spaces,
		maxResults,
		showCommentsCount,
		showLikesCount,
		blankTitle,
		blankDescription,
		contentBlueprintId,
		blueprintModuleCompleteKey,
		createButtonLabel,
		labelsError,
		macroParamsForQuery,
	} = useParsedMacroParams(macroParams);

	const { items, loading, error } = useContentReportTableData(macroParamsForQuery);

	if (labelsError) {
		return <ErrorDisplay error={new Error(formatMessage(i18n.errorLabelsRequired))} />;
	}

	if (error) {
		return <ErrorDisplay error={error} />;
	}

	if (loading || items === undefined) {
		return (
			<Box xcss={styles.loadingContainer} testId="content-report-table-loading">
				<Spinner size="medium" label="Loading content report table" />
			</Box>
		);
	}

	const hasBlueprintExperience = items.length === 0 && Boolean(blueprintModuleCompleteKey);

	if (items.length === 0) {
		// Build create content URL for blueprint blank experience
		const createContentUrl = buildCreateContentUrl({
			hasBlueprintExperience,
			contentBlueprintId,
			spaceKey: macroDefaultProps.spaceKey,
		});

		return (
			<EmptyState
				blankTitle={blankTitle}
				blankDescription={blankDescription}
				createButtonLabel={createButtonLabel}
				createContentUrl={createContentUrl}
				hasBlueprintExperience={hasBlueprintExperience}
			/>
		);
	}

	const hasSocialColumn = showCommentsCount || showLikesCount;
	const showMoreResults = items.length >= maxResults;

	// Build "Find more results" URL
	const showMoreResultsUrl = showMoreResults
		? buildFindMoreResultsUrl({
				labels,
				spaces,
			})
		: null;

	return (
		<ContentReportTable
			items={items}
			hasSocialColumn={hasSocialColumn}
			showCommentsCount={showCommentsCount}
			showLikesCount={showLikesCount}
			canViewProfiles={canViewProfiles}
			showMoreResultsUrl={showMoreResultsUrl}
			macroId={
				macroDefaultProps?.attributes?.macroId
					? String(macroDefaultProps.attributes.macroId)
					: undefined
			}
		/>
	);
};

const renderErrorFallback = () => <FormattedMessage {...i18n.error} />;

export const ContentReportTableMacroRenderer = (props: ContentReportTableMacroRendererProps) => {
	return (
		<GenericErrorBoundary
			renderOnError={renderErrorFallback}
			attribution={Attribution.PAGE_EXTENSIONS}
		>
			<ContentReportTableMacroRendererComponent {...props} />
		</GenericErrorBoundary>
	);
};
